"use client";
import { useEffect, useRef, useState } from "react";
import { Check, Copy, FileText, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { SaveButtonSpinner } from "@/components/save-button-spinner";

export function DocsPageTools({ href }: { href: string }) {
  const [state, setState] = useState<"idle" | "loading" | "copied" | "error">(
    "idle",
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef(false);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  async function copy() {
    if (pending.current) return;
    pending.current = true;
    if (timer.current) clearTimeout(timer.current);
    setState("loading");
    try {
      const response = await fetch(`${href}.md`);
      if (!response.ok) throw new Error("Unable to load Markdown");
      await navigator.clipboard.writeText(await response.text());
      setState("copied");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setState("idle"), 1800);
    } catch {
      setState("error");
    } finally {
      pending.current = false;
    }
  }
  return (
    <div className="docs-page-tools docs-controls relative shrink-0 text-xs text-muted-foreground">
      <div className="docs-copy-control flex h-7 items-stretch rounded-md border border-input bg-transparent">
        <button
          type="button"
          aria-label="Copy page"
          onClick={() => void copy()}
          disabled={state === "loading"}
          className="inline-flex items-center justify-center gap-1.5 rounded-l-[inherit] px-1.5 transition-colors hover:bg-sidebar-accent focus-visible:outline-none disabled:cursor-wait sm:px-2"
        >
          <span className="relative size-3.5">
            <Copy
              className={`absolute size-3.5 transition-opacity duration-200 ${state === "copied" ? "opacity-0" : "opacity-100"}`}
            />
            <Check
              className={`absolute size-3.5 text-green-500 transition-opacity duration-200 ${state === "copied" ? "opacity-100" : "opacity-0"}`}
            />
          </span>
          <span className="hidden sm:inline">Copy</span>
          <SaveButtonSpinner show={state === "loading"} collapseGap />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Page actions"
              className="flex w-6 items-center justify-center rounded-r-[inherit] border-l border-input transition-colors hover:bg-sidebar-accent focus-visible:outline-none"
            >
              <ChevronDown className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="docs-controls w-44">
            <DropdownMenuItem asChild>
              <a href={`${href}.md`}>
                <FileText />
                View Markdown
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <span
        role="status"
        className={
          state === "error"
            ? "absolute right-0 top-full z-20 mt-2 w-52 rounded-md border border-input bg-card p-2 text-xs text-destructive shadow-sm"
            : "sr-only"
        }
      >
        {state === "copied"
          ? "Page copied."
          : state === "error"
            ? "Could not copy. Open Markdown to copy the page manually."
            : ""}
      </span>
    </div>
  );
}
